// A tela de acesso mostra DUAS camadas sobrepostas que não podem viver na
// mesma lista de marcações:
//
//   travadas — o que o perfil SELECIONADO concede. Derivada do perfil, nunca
//              editada à mão. Trocar de perfil recalcula o conjunto inteiro.
//   manual   — o que alguém concedeu a esta pessoa por fora de qualquer perfil.
//              É dela, e nenhuma operação de perfil encosta.
//
// Guardar as duas numa lista só de `acesso: true/false` é o que produziu o
// bug: ao trocar de perfil não havia como saber quais marcações tinham vindo
// do perfil anterior, então o resto dele ficava para trás e era gravado como
// se fosse concessão manual. A tela de criação (`AddUsuario`) já separava as
// camadas assim e por isso nunca teve o problema — estas funções levam o mesmo
// modelo para a tela de alteração.

const paraConjunto = v => {
  if (v instanceof Set) return v;
  if (Array.isArray(v)) return new Set(v);
  return new Set();
};

// Percorre os módulos da tela e devolve só o que é concessão manual.
//
// A sutileza está na órfã: uma linha gravada com `origem='perfil'` que o perfil
// vigente não concede mais (alguém editou o perfil depois). Ela não é do perfil
// atual nem foi marcada à mão, e a leitura ingênua a deixaria fora das duas
// camadas — some calada no salvar, que é a perda de dado do #356. Aqui ela
// conta como manual: preservar acesso que existe é sempre a resposta segura.
export function camadaManual({ modulos, funcionalidadesDoPerfilVigente } = {}) {
  const doPerfil = paraConjunto(funcionalidadesDoPerfilVigente);
  const manual = new Set();

  (Array.isArray(modulos) ? modulos : []).forEach(mod => {
    const funcs = (mod || {}).funcionalidades;
    (Array.isArray(funcs) ? funcs : []).forEach(f => {
      if (!f || f.acesso !== true) return;
      if (f.origem === 'perfil' && doPerfil.has(f.id)) return;
      manual.add(f.id);
    });
  });

  return manual;
}

// O que o botão Alterar grava: a união das duas camadas. Ordenado para o
// payload ficar estável entre chamadas — facilita comparar em log e em teste.
export function conjuntoParaGravar({ manual, travadas } = {}) {
  return [...new Set([...paraConjunto(manual), ...paraConjunto(travadas)])].sort(
    (a, b) => a - b
  );
}

// O perfil concede por id, sem olhar gerência; a tela lista só os módulos
// liberados para a gerência da pessoa (`gerencia_modulo`). O que cai nessa
// diferença é concedido de verdade e não aparece em lugar nenhum — a tela tem
// que dizer isso em vez de deixar o número mentir.
export function funcionalidadesForaDaTela({ travadas, modulos } = {}) {
  const doPerfil = paraConjunto(travadas);
  if (doPerfil.size === 0) return [];

  const visiveis = new Set();
  (Array.isArray(modulos) ? modulos : []).forEach(mod => {
    const funcs = (mod || {}).funcionalidades;
    (Array.isArray(funcs) ? funcs : []).forEach(f => {
      if (f) visiveis.add(f.id);
    });
  });

  return [...doPerfil].filter(id => !visiveis.has(id)).sort((a, b) => a - b);
}
